import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon, CalendarPlusIcon } from 'lucide-react';
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type FormStatus = 'idle' | 'loading' | 'success' | 'error';
const serviceOptions = ['Customised Massage — 30 min (150 RON)', 'Customised Massage — 60 min (250 RON)', 'Customised Massage — 90 min (350 RON)', 'Pressotherapy — 30 min (100 RON)', 'Pressotherapy with Face & Head Massage — 30 min (180 RON)', 'Jade & Tourmaline Massage Bed — 40 min (100 RON)', 'Body Peeling Delight — 30 min (100 RON)', 'Sauna, Jacuzzi & Hot Tub — 90 min (150 RON/person, min. 2)', '"Zen Barn" Ritual — 120 min (500 RON)', '"Golden Body Glow" Ritual — 120 min (700 RON)'];
const timeSlotHours: Record<string, {
  start: number;
  end: number;
}> = {
  'Morning (9:00–12:00)': {
    start: 9,
    end: 12
  },
  'Afternoon (12:00–16:00)': {
    start: 12,
    end: 16
  },
  'Evening (16:00–20:00)': {
    start: 16,
    end: 20
  }
};
function generateICS(eventDate: string, timeSlot: string, serviceName: string, guestName: string): string {
  const hours = timeSlotHours[timeSlot] || {
    start: 9,
    end: 12
  };
  const d = new Date(eventDate);
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const dtStart = `${year}${month}${day}T${pad(hours.start)}0000`;
  const dtEnd = `${year}${month}${day}T${pad(hours.end)}0000`;
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Zen Barn Spa//Booking//EN', 'BEGIN:VEVENT', `DTSTART;TZID=Europe/Bucharest:${dtStart}`, `DTEND;TZID=Europe/Bucharest:${dtEnd}`, `SUMMARY:Zen Barn Spa — ${serviceName}`, `DESCRIPTION:Booking for ${guestName}\\n${serviceName}\\nStrada Principală nr. 376\\, Richiș`, 'LOCATION:Strada Principală nr. 376\\, Richiș\\, Sibiu County\\, Romania', 'STATUS:TENTATIVE', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
}
function getGoogleCalendarUrl(eventDate: string, timeSlot: string, serviceName: string, guestName: string): string {
  const hours = timeSlotHours[timeSlot] || {
    start: 9,
    end: 12
  };
  const d = new Date(eventDate);
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  // Google Calendar uses UTC-style dates but we pass the local time
  const dtStart = `${year}${month}${day}T${pad(hours.start)}0000`;
  const dtEnd = `${year}${month}${day}T${pad(hours.end)}0000`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Zen Barn Spa — ${serviceName}`,
    dates: `${dtStart}/${dtEnd}`,
    ctz: 'Europe/Bucharest',
    details: `Booking for ${guestName}\n${serviceName}\nStrada Principală nr. 376, Richiș`,
    location: 'Strada Principală nr. 376, Richiș, Sibiu County, Romania'
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
const timeSlots = ['Morning (9:00–12:00)', 'Afternoon (12:00–16:00)', 'Evening (16:00–20:00)'];
export function BookingModal({
  isOpen,
  onClose
}: BookingModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [service, setService] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Get today's date in YYYY-MM-DD format for min attribute
  const today = new Date().toISOString().split('T')[0];
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Focus first input after animation
      setTimeout(() => nameInputRef.current?.focus(), 300);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  const resetForm = () => {
    setName('');
    setPhone('');
    setDate('');
    setTime('');
    setService('');
    setStatus('idle');
    setErrorMsg('');
  };
  const handleClose = () => {
    onClose();
    // Reset after animation completes
    setTimeout(resetForm, 300);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    const formData = new FormData();
    formData.append('Guest Name', name);
    formData.append('Phone Number', phone);
    formData.append('Preferred Date', date);
    formData.append('Preferred Time', time);
    formData.append('Requested Service', service);
    formData.append('_subject', 'New Booking Request — Zen Barn Spa');
    formData.append('_captcha', 'false');
    formData.append('_template', 'table');
    try {
      const response = await fetch('https://formsubmit.co/ajax/elenabuzatu1981@gmail.com', {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Could not send your request. Please check your connection and try again.');
    }
  };
  const inputClasses = 'w-full px-4 py-3 rounded-xl border border-stone bg-warm-white font-body text-bark text-base placeholder:text-bark-light/50 focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 transition-colors duration-200';
  const labelClasses = 'block font-body text-sm font-medium text-bark mb-1.5';
  return <AnimatePresence data-id="element-55">
      {isOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} data-id="element-56">
          {/* Backdrop */}
          <motion.div className="absolute inset-0" style={{
        backgroundColor: 'rgba(61,50,41,0.6)',
        backdropFilter: 'blur(8px)'
      }} onClick={handleClose} aria-hidden="true" data-id="element-57" />

          {/* Modal card */}
          <motion.div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-cream rounded-2xl shadow-2xl" initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} role="dialog" aria-modal="true" aria-label="Book a session" data-id="element-58">
            {/* Close button */}
            <button onClick={handleClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-stone/50 flex items-center justify-center text-bark-light hover:text-bark hover:bg-stone transition-colors duration-200 z-10" aria-label="Close booking form" data-id="element-59">
              <XIcon className="w-5 h-5" data-id="element-60" />
            </button>

            <div className="p-8 md:p-10" data-id="element-61">
              {/* ── IDLE / FORM STATE ── */}
              {(status === 'idle' || status === 'loading' || status === 'error') && <>
                  <div className="mb-8" data-id="element-62">
                    <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-3" data-id="element-63">
                      Reservation
                    </p>
                    <h2 className="font-heading text-3xl md:text-4xl font-semibold text-bark mb-2" data-id="element-64">
                      Book a Session
                    </h2>
                    <p className="font-body text-bark-light text-base leading-relaxed" data-id="element-65">
                      Fill in the details and we will get back to you shortly.
                    </p>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" data-id="element-66">
                    {/* Name */}
                    <div data-id="element-67">
                      <label htmlFor="booking-name" className={labelClasses} data-id="element-68">
                        Name
                      </label>
                      <input ref={nameInputRef} id="booking-name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" className={inputClasses} data-id="element-69" />
                    </div>

                    {/* Phone */}
                    <div data-id="element-70">
                      <label htmlFor="booking-phone" className={labelClasses} data-id="element-71">
                        Phone Number
                      </label>
                      <input id="booking-phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+40 7XX XXX XXX" className={inputClasses} data-id="element-72" />
                    </div>

                    {/* Date & Time row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-73">
                      <div data-id="element-74">
                        <label htmlFor="booking-date" className={labelClasses} data-id="element-75">
                          Preferred Date
                        </label>
                        <input id="booking-date" type="date" required min={today} value={date} onChange={e => setDate(e.target.value)} className={inputClasses} data-id="element-76" />
                      </div>
                      <div data-id="element-77">
                        <label htmlFor="booking-time" className={labelClasses} data-id="element-78">
                          Preferred Time
                        </label>
                        <select id="booking-time" required value={time} onChange={e => setTime(e.target.value)} className={inputClasses + (!time ? ' text-bark-light/50' : '')} data-id="element-79">
                          <option value="" disabled data-id="element-80">
                            Select a time
                          </option>
                          {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-81">
                              {slot}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    {/* Service */}
                    <div data-id="element-82">
                      <label htmlFor="booking-service" className={labelClasses} data-id="element-83">
                        Desired Service
                      </label>
                      <select id="booking-service" required value={service} onChange={e => setService(e.target.value)} className={inputClasses + (!service ? ' text-bark-light/50' : '')} data-id="element-84">
                        <option value="" disabled data-id="element-85">
                          Choose a treatment
                        </option>
                        {serviceOptions.map(opt => <option key={opt} value={opt} data-id="element-86">
                            {opt}
                          </option>)}
                      </select>
                    </div>

                    {/* Error message */}
                    {status === 'error' && <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200" data-id="element-87">
                        <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-88" />
                        <p className="font-body text-sm text-red-700" data-id="element-89">
                          {errorMsg}
                        </p>
                      </div>}

                    {/* Submit */}
                    <button type="submit" disabled={status === 'loading'} className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-terracotta text-white font-body font-medium text-sm tracking-wide rounded-full hover:bg-terracotta-dark transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-cream disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-90">
                      {status === 'loading' ? <>
                          <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-91" />
                          Sending...
                        </> : 'Send Booking Request'}
                    </button>
                  </form>
                </>}

              {/* ── SUCCESS STATE ── */}
              {status === 'success' && <motion.div className="flex flex-col items-center text-center py-8" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-92">
                  <div className="w-16 h-16 rounded-full bg-sage/15 flex items-center justify-center mb-6" data-id="element-93">
                    <CheckCircleIcon className="w-9 h-9 text-sage" data-id="element-94" />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold text-bark mb-3" data-id="element-95">
                    Request Sent!
                  </h3>
                  <p className="font-body text-bark-light text-base leading-relaxed mb-2 max-w-sm" data-id="element-96">
                    Thank you, {name}! We received your booking request and will
                    confirm your session shortly.
                  </p>
                  <p className="font-body text-bark-light/60 text-sm mb-8" data-id="element-97">
                    We will contact you at {phone} to confirm.
                  </p>

                  {/* Add to Calendar */}
                  <div className="w-full max-w-xs space-y-3 mb-8" data-id="element-98">
                    <p className="font-body text-sm font-medium text-bark mb-2" data-id="element-99">
                      Add to your calendar
                    </p>
                    <a href={getGoogleCalendarUrl(date, time, service, name)} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-stone rounded-full font-body text-sm font-medium text-bark hover:border-terracotta hover:text-terracotta transition-colors duration-300" data-id="element-100">
                      <CalendarPlusIcon className="w-4 h-4" data-id="element-101" />
                      Google Calendar
                    </a>
                    <button onClick={() => {
                const ics = generateICS(date, time, service, name);
                const blob = new Blob([ics], {
                  type: 'text/calendar;charset=utf-8'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'zen-barn-spa-booking.ics';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }} className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-stone rounded-full font-body text-sm font-medium text-bark hover:border-terracotta hover:text-terracotta transition-colors duration-300" data-id="element-102">
                      <CalendarPlusIcon className="w-4 h-4" data-id="element-103" />
                      Apple / Outlook (.ics)
                    </button>
                  </div>

                  <button onClick={handleClose} className="px-8 py-3 bg-bark text-cream font-body font-medium text-sm tracking-wide rounded-full hover:bg-bark/90 transition-colors duration-300" data-id="element-104">
                    Close
                  </button>
                </motion.div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}